import type { LearnTopic } from "../topic-types";

/**
 * Autism & Trauma — Learn Hub topic page.
 *
 * Neurodiversity-affirming and trauma-informed. Autism is presented as a
 * neurotype, never as something caused by trauma, and never as something to
 * be fixed. The content is educational and explicitly non-diagnostic.
 */
export const AUTISM_TRAUMA_TOPIC: LearnTopic = {
  slug: "autism-trauma",
  emoji: "🧩",
  title: "Autism & Trauma",
  tagline: "Two different things that can live in the same person",
  theme: "sage",
  category: "understanding-trauma",
  isNew: true,
  seoDescription:
    "A neurodiversity-affirming, trauma-informed guide to autism and trauma — how they differ, why trauma is often missed in autistic people, sensory overwhelm, masking, autistic burnout, and regulation that actually fits. Educational, never diagnostic. From Recovery Trauma™.",
  cardBlurb:
    "How autism and trauma differ, why trauma is often missed, and regulation that actually fits your nervous system.",
  intro: [
    "Autism is a neurotype — a lifelong difference in how a person perceives, processes and communicates. Trauma is what happens inside a nervous system after overwhelming experience. They are not the same thing, and autism is not caused by trauma.",
    "Autistic people can, and often do, also experience trauma. Living in environments built for a different neurotype — being misread, corrected, excluded or overloaded, year after year — can leave real trauma responses behind. Both can be true at once, and each deserves to be understood on its own terms.",
    "Nothing in this section asks you to mask, to tolerate more than you can, or to regulate in ways that were designed for someone else's nervous system. Take what fits and leave the rest.",
    "Please read this as education and self-reflection only. It does not diagnose autism, PTSD, C-PTSD or any other condition, and it is not a substitute for assessment, therapy or treatment from an appropriately qualified professional.",
  ],
  closing:
    "You are not a broken version of anyone. Much of what gets called 'too much' or 'too sensitive' is a nervous system doing exactly what it was built to do, in surroundings that ask more of it than they give back. Understanding the difference between your neurotype and your trauma responses is often the point where self-blame starts to loosen.",
  continueTo: [
    { label: "Grounding techniques", to: "/learn/grounding-techniques", note: "Adapt these to your own sensory profile" },
    { label: "Window of Tolerance", to: "/learn/window-of-tolerance", note: "Capacity, overwhelm and shutdown" },
    { label: "Trauma responses", to: "/learn/trauma-responses" },
    { label: "Self-Assessments", to: "/learn/assessments", note: "Reflection tools — never diagnoses" },
  ],
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What autism is, what trauma is, and why the two get confused.",
      intro:
        "Education first. Nothing here is a diagnosis, and nothing here asks you to look inward yet.",
      resources: [
        {
          slug: "autism-vs-trauma",
          kind: "learn",
          title: "Autism and trauma — similarities and differences",
          summary: "Overlapping surface, very different roots.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Autism is a neurodevelopmental difference that is present across a person's whole life. It shapes sensory processing, communication, social intuition, attention and the need for predictability. Trauma is the lasting imprint of experience that overwhelmed a person's capacity to cope — it has a before and an after.",
            },
            {
              type: "prose",
              text: "The confusion happens because some outward signs look alike: sensitivity to noise or touch, difficulty in groups, shutdown, exhaustion, a strong need for control over the environment. The same behaviour can come from a lifelong neurotype, from a trauma response, or from both together — and the difference matters, because the support each one needs is different.",
            },
            {
              type: "list",
              title: "Where they tend to differ",
              items: [
                "Timeline: autistic traits are lifelong; trauma responses usually have a point where things changed",
                "Sensory difficulty: autistic sensory processing is constant and specific; trauma-linked sensitivity often tracks threat cues and fluctuates with stress",
                "Social difference: autistic social processing differs by design; social withdrawal after trauma is often protective avoidance",
                "Routine: predictability is regulating for many autistic people at all times, not only after frightening events",
              ],
            },
            {
              type: "list",
              title: "Where they genuinely overlap",
              items: [
                "Nervous-system overwhelm and difficulty coming back down",
                "Exhaustion that rest does not fully repair",
                "Shutdown, numbness or losing access to speech",
                "Hyper-awareness of other people's moods and of the environment",
              ],
            },
            {
              type: "note",
              text: "Autism is not caused by trauma, and trauma does not make someone autistic. This page cannot tell you which is which for you — only an appropriately qualified professional can assess that, and many autistic adults find an assessor who is neurodiversity-affirming makes a real difference.",
            },
          ],
        },
        {
          slug: "why-trauma-is-missed",
          kind: "learn",
          title: "Why trauma is often overlooked or misread in autistic people",
          summary: "When distress is explained away as 'just autism'.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Diagnostic overshadowing is the name for what happens when everything a person experiences gets attributed to one label. Once someone is known to be autistic, distress can be read as a trait rather than a signal — so the trauma underneath is never asked about.",
            },
            {
              type: "list",
              title: "Common ways it gets missed",
              items: [
                "Flashbacks or panic read as 'sensory issues' and left there",
                "Withdrawal after a frightening event read as ordinary autistic preference for solitude",
                "Distress expressed differently — through movement, sound or silence — not recognised as distress",
                "A flat or unusual tone when describing frightening events taken to mean it was not serious",
                "Masking so effective that no one sees anything is wrong until collapse",
                "Being disbelieved historically, so the person stops reporting anything at all",
              ],
            },
            {
              type: "prose",
              text: "The cost of this is not only unmet support. Being told repeatedly that what you feel is not real, or is simply how you are, is itself invalidating — and chronic invalidation is one of the ways trauma builds.",
            },
          ],
        },
        {
          slug: "sensory-and-nervous-system",
          kind: "learn",
          title: "Sensory overwhelm and the nervous system",
          summary: "Why input adds up, and what happens when it does.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Many autistic people take in more sensory information, filter less of it automatically, and register it more intensely. A strip light, a background conversation, a label in a collar and an unexpected question are not separate small things — they stack, and the nervous system pays for each one.",
            },
            {
              type: "flow",
              title: "How overload usually builds",
              items: [
                "Input accumulates through the day",
                "Filtering and coping take more effort",
                "Capacity narrows — small things start to cost a lot",
                "Threshold reached",
                "Meltdown, shutdown or collapse",
              ],
            },
            {
              type: "prose",
              text: "Trauma sits on top of this. A nervous system already running near its ceiling has very little room left when a threat cue arrives, which is one reason overwhelm and trauma responses can be hard to tell apart from the inside.",
            },
            {
              type: "cta",
              text: "The Window of Tolerance module explains capacity, overwhelm and shutdown in more depth.",
              label: "Open Window of Tolerance",
              to: "/learn/window-of-tolerance",
            },
          ],
        },
        {
          slug: "meltdowns-shutdowns-freeze",
          kind: "learn",
          title: "Meltdowns, shutdowns and freeze responses",
          summary: "What each one is, and what actually helps.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "A meltdown is an involuntary response to overload. It is not a tantrum, not manipulation and not a choice — the system has passed the point where it can hold any more. A shutdown is the same overload expressed inwardly: withdrawal, stillness, loss of speech, a sense of the world going far away.",
            },
            {
              type: "prose",
              text: "Freeze is a trauma response — the body immobilising in the presence of perceived threat. Shutdown and freeze can look almost identical from the outside, and can happen together. Neither responds well to being questioned, reasoned with or hurried.",
            },
            {
              type: "list",
              title: "What tends to help during and after",
              items: [
                "Less input, not more — dim the light, lower the noise, reduce demands, including conversation",
                "No questions that need an answer; short statements or silence",
                "Familiar sensory anchors: a weighted blanket, a known object, a favourite sound at low volume",
                "Recovery time afterwards, without a debrief, an apology or a post-mortem",
                "Understanding this as a body event, not a behaviour problem",
              ],
            },
            {
              type: "note",
              text: "If meltdowns or shutdowns involve harm to yourself or others, or leave you unable to keep yourself safe, please reach out for real-world support rather than working through this alone.",
            },
          ],
        },
      ],
    },
    {
      slug: "experiences",
      emoji: "🪞",
      title: "Experiences that can leave a mark",
      blurb: "Masking, exclusion, invalidation, and life without predictability.",
      intro:
        "None of what follows is inevitable, and none of it is anyone's fault. These are patterns many autistic people describe.",
      resources: [
        {
          slug: "masking",
          kind: "learn",
          title: "Masking and its emotional impact",
          summary: "The cost of continuously translating yourself.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Masking — camouflaging — is the effort of suppressing natural responses and performing an expected way of being: holding eye contact that hurts, stilling movement that regulates, rehearsing conversation, hiding confusion, matching an energy that is not yours. It is often learned early, and often for good reason: it reduces punishment, ridicule and exclusion.",
            },
            {
              type: "list",
              title: "What long-term masking often costs",
              items: [
                "Exhaustion that is out of proportion to what the day appeared to contain",
                "Losing track of what you actually feel, want or prefer",
                "Feeling like a fraud even in relationships that are safe",
                "Delayed collapse — holding it together at work or school, then falling apart at home",
                "Support arriving late, because nobody could see the struggle",
              ],
            },
            {
              type: "prose",
              text: "Unmasking is not a switch, and it is not always safe to do everywhere. Many people start with one place, one person, or one hour where nothing has to be performed.",
            },
          ],
        },
        {
          slug: "rejection-and-invalidation",
          kind: "learn",
          title: "Social rejection, bullying and chronic invalidation",
          summary: "Repeated small wounds add up to something real.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Trauma is not only made of single catastrophic events. Being left out, mocked for how you speak or move, corrected constantly, told you are too much or too sensitive, disbelieved when you describe your own experience — repeated over years, this shapes a nervous system just as surely as one frightening moment does.",
            },
            {
              type: "list",
              title: "How it can show up later",
              items: [
                "Expecting rejection before it happens, and withdrawing first",
                "Intense pain in response to criticism or perceived exclusion",
                "Apologising reflexively for existing, taking up space or asking for anything",
                "Difficulty trusting warmth, or waiting for it to be withdrawn",
                "Deep doubt about your own perception of events",
              ],
            },
            {
              type: "cta",
              text: "If you recognise the pattern of putting everyone else first to stay safe, this module goes further.",
              label: "Open People-Pleasing",
              to: "/learn/people-pleasing",
            },
          ],
        },
        {
          slug: "being-misunderstood",
          kind: "learn",
          title: "Trauma from being misunderstood or unsupported",
          summary: "When help itself became the hard part.",
          minutes: 3,
          blocks: [
            {
              type: "list",
              title: "Experiences people describe",
              items: [
                "Therapies that treated natural regulation, like stimming, as a symptom to remove",
                "Support that was conditional on appearing 'normal enough'",
                "Being told a need was a preference, or a preference was defiance",
                "Years of being assessed, described and discussed without being asked",
                "Reaching out and being turned away, then learning not to reach out",
              ],
            },
            {
              type: "prose",
              text: "When the places meant to help have caused harm, caution about support is a reasonable conclusion, not resistance. Trust rebuilds slowly, at whatever pace the evidence allows.",
            },
          ],
        },
        {
          slug: "change-and-safety",
          kind: "learn",
          title: "Change, unpredictability and feelings of safety",
          summary: "Why predictability is regulation, not rigidity.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Routine, sameness and knowing what comes next do real work for many autistic nervous systems: they reduce how much has to be processed in the moment, freeing capacity for everything else. When plans change without warning, that support is removed at exactly the moment more is being asked.",
            },
            {
              type: "list",
              title: "Things that often help",
              items: [
                "Advance notice, and the reason for a change where possible",
                "Knowing the shape of an event: how long, who, where, how it ends",
                "An agreed way to leave early that costs nothing socially",
                "Keeping one or two anchors unchanged when much else is shifting",
              ],
            },
            {
              type: "prose",
              text: "For someone who also carries trauma, unpredictability can register as danger rather than inconvenience. The response is proportionate to what the body believes is at stake.",
            },
          ],
        },
      ],
    },
    {
      slug: "burnout-and-communication",
      emoji: "🌒",
      title: "Burnout, exhaustion and communication",
      blurb: "Two kinds of depletion, and what happens to words under stress.",
      resources: [
        {
          slug: "autistic-burnout",
          kind: "learn",
          title: "Autistic burnout vs trauma-related exhaustion",
          summary: "Related, overlapping, not identical.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "Autistic burnout tends to involve",
              items: [
                "Accumulated demand: masking, sensory load, life admin, too little recovery",
                "Loss of skills that were previously available — speech, cooking, driving, replying to messages",
                "Sharply increased sensory sensitivity",
                "Improvement with reduced demands, sensory rest and permission to unmask",
              ],
            },
            {
              type: "list",
              title: "Trauma-related exhaustion tends to involve",
              items: [
                "A body that has been braced or on alert for a long time",
                "Sleep that does not restore; waking already tired",
                "Emotional numbness alongside the fatigue",
                "Improvement as safety, regulation and processing build over time",
              ],
            },
            {
              type: "prose",
              text: "Many people have both at once. The practical distinction is what the exhaustion responds to: burnout eases when demand and sensory load come down, while trauma-related exhaustion also needs a growing sense of safety.",
            },
            {
              type: "note",
              text: "Persistent exhaustion has medical causes too. Please have it looked at properly rather than assuming it must be one of these.",
            },
          ],
        },
        {
          slug: "communication-under-stress",
          kind: "learn",
          title: "Communication differences during stress",
          summary: "When words become expensive or unavailable.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Under high stress, spoken language is often the first thing to go. Situational mutism, long processing delays, giving short or flat answers, or answering with what seems expected rather than what is true — none of these mean someone is being difficult, evasive or unaffected.",
            },
            {
              type: "list",
              title: "What supports communication when capacity is low",
              items: [
                "Writing, typing or texting instead of speaking",
                "Yes/no or multiple-choice questions rather than open ones",
                "Real time to answer — silence left alone, not filled",
                "Agreed signals for 'I need to stop' and 'I need a minute'",
                "Coming back to the conversation later, by agreement, rather than pushing through",
              ],
            },
            {
              type: "prose",
              text: "It can help to prepare a short note in advance — what you need, what does not help — for appointments or moments where speaking may not be available.",
            },
          ],
        },
      ],
    },
    {
      slug: "safety-and-relationships",
      emoji: "🛡️",
      title: "Boundaries, consent and safety",
      blurb: "Why some autistic people are targeted, and what strengthens safety.",
      resources: [
        {
          slug: "boundaries-consent-vulnerability",
          kind: "learn",
          title: "Boundaries, consent and vulnerability to abusive relationships",
          summary: "Not naivety — grooming, compliance training and unmet need.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Autistic adults are, as a group, at raised risk of exploitation and abuse. This is not because of any deficit in judgement. It is because a lifetime of being told to comply, to be polite, to tolerate discomfort and to doubt your own reading of a situation removes the very signals that boundaries depend on — and because people who exploit others look for isolation and for someone used to being disbelieved.",
            },
            {
              type: "list",
              title: "Factors that raise risk",
              items: [
                "Being taught early that compliance keeps you safe and objection causes trouble",
                "Loneliness after long exclusion, so any attention feels valuable",
                "Having your own perception overruled so often that you stop trusting it",
                "Unclear or unspoken social rules being used against you deliberately",
                "Financial or care dependence on the person causing harm",
              ],
            },
            {
              type: "list",
              title: "What strengthens safety",
              items: [
                "Explicit permission — from yourself — to say no without a reason or an apology",
                "Deciding limits in advance, in writing, when the situation is calm",
                "Treating body signals of dread or exhaustion as information worth acting on",
                "Keeping at least one person outside the relationship who knows what is happening",
                "Noticing pressure to keep things secret, and treating that as a warning sign in itself",
              ],
            },
            {
              type: "cta",
              text: "The Boundaries module has practical scripts and step-by-step practice.",
              label: "Open Boundaries",
              to: "/learn/boundaries",
            },
            {
              type: "note",
              text: "If you are currently unsafe with someone, please prioritise real-world support over any online resource, including this one.",
            },
          ],
        },
      ],
    },
    {
      slug: "regulation",
      emoji: "🌿",
      title: "Regulation that fits you",
      blurb: "Grounding adapted for autistic nervous systems — and what to do when the usual advice fails.",
      intro:
        "There is no correct way to regulate. The right practice is the one your body actually settles with.",
      resources: [
        {
          slug: "adapted-grounding",
          kind: "practice",
          title: "Grounding and regulation adapted for autistic people",
          summary: "Build from what already works for you.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "Most grounding advice assumes a nervous system that is soothed by noticing sensation. If noticing sensation is already the overwhelming part, that advice can make things worse. Adapted grounding starts from your own profile: what settles you, what overloads you, and what you already do instinctively.",
            },
            {
              type: "steps",
              title: "A gentler sequence",
              items: [
                { label: "Reduce input first", note: "Leave, dim, mute, unclench. Subtraction before addition." },
                { label: "Let stimming happen", note: "Rocking, flapping, pacing, humming and fidgeting are regulation. Do not interrupt them." },
                { label: "Add deep pressure", note: "Weighted blanket, firm cushion, a tight hug from yourself, pressing palms together." },
                { label: "Use one predictable rhythm", note: "Counting, a familiar song on repeat, walking at a steady pace, a repeated phrase." },
                { label: "Choose one sense, not five", note: "A single known sensory anchor beats a scan of everything around you." },
                { label: "Recover longer than feels reasonable", note: "Coming down takes far more time than going up." },
              ],
            },
            {
              type: "cta",
              text: "Try the grounding library and keep only what your body agrees with.",
              label: "Open Grounding",
              to: "/grounding",
            },
            {
              type: "cta",
              text: "Low, steady sound can be regulating for some people and unbearable for others — test at low volume.",
              label: "Open Sounds & Meditations",
              to: "/sounds",
            },
          ],
        },
        {
          slug: "when-calming-doesnt-work",
          kind: "learn",
          title: "Why some common calming exercises may not work for you",
          summary: "Nothing has gone wrong if the standard advice backfires.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "Common advice, and why it can misfire",
              items: [
                "Close your eyes — removes visual anchoring and can increase overwhelm or dissociation",
                "Body scans — turning attention inward can intensify sensory distress or interoceptive confusion",
                "Slow, controlled breathing — unclear internal signals can make breath focus alarming rather than calming",
                "Sit still — stillness blocks the movement many nervous systems use to regulate",
                "Notice five things you can see — a scanning task added to a system already overloaded by input",
                "Group relaxation sessions — social processing costs more than the exercise gives back",
              ],
            },
            {
              type: "prose",
              text: "If a practice reliably makes things worse, that is useful data about your nervous system, not a failure of effort. Keep eyes open, move, use pressure and rhythm, shorten the practice, or skip it entirely and use something you already know works.",
            },
            {
              type: "cta",
              text: "Breathing practices with visual pacing can be easier than counting internally.",
              label: "Open Breathing",
              to: "/breathing",
            },
          ],
        },
        {
          slug: "sensory-safe-environments",
          kind: "learn",
          title: "Creating sensory-safe environments",
          summary: "Changing the room is a legitimate intervention.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "Small changes with large returns",
              items: [
                "Lighting: warm lamps instead of overhead strip lighting; reduce flicker and glare",
                "Sound: ear defenders, loop earplugs, a predictable background sound rather than unpredictable noise",
                "Touch: seams, labels and fabrics chosen deliberately, not tolerated",
                "Space: one low-demand room or corner that is always available and never negotiated",
                "Smell: unscented products where possible; strong fragrance removed rather than covered",
                "Time: buffer periods after demanding events, protected in the diary like appointments",
              ],
            },
            {
              type: "prose",
              text: "Accommodations are not indulgences. Lowering the baseline load is often the single most effective thing available — and it leaves capacity for everything else, including trauma recovery.",
            },
          ],
        },
        {
          slug: "reflect",
          kind: "reflect",
          title: "Reflection: my own map",
          summary: "Gentle prompts. Skip anything that does not fit.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              title: "Prompts",
              items: [
                "Which parts of my experience have been with me for as long as I can remember?",
                "Which parts began after something specific happened?",
                "What does my body do when it has had enough — and what does it need next?",
                "Where and with whom do I mask most? What would one hour of not masking look like?",
                "Which calming techniques have made things worse, and what do I want to stop trying?",
                "What already helps that I have been treating as unimportant or embarrassing?",
                "What change in my environment would give me the most back for the least effort?",
              ],
            },
            {
              type: "cta",
              text: "Your journal is private to you. Nothing written there is shared or reviewed.",
              label: "Open Journal",
              to: "/journal",
            },
            {
              type: "note",
              text: "Reflection is not assessment. Answering these tells you about your experience, not about any diagnosis.",
            },
          ],
        },
        {
          slug: "sensory-safety-plan",
          kind: "worksheet",
          title: "My sensory and regulation plan",
          summary: "One page you can hand to someone, or keep for yourself.",
          minutes: 10,
          worksheetFields: [
            { label: "Sounds, lights, textures or smells that overload me", lines: 3 },
            { label: "Early signs I am approaching my limit", hint: "In my body, my words, my patience", lines: 3 },
            { label: "What helps me before I reach the limit", lines: 3 },
            { label: "What helps during a meltdown or shutdown", hint: "Include what does NOT help", lines: 4 },
            { label: "What I need afterwards, and for how long", lines: 3 },
            { label: "My signal for 'I need to stop or leave'", lines: 2 },
            { label: "How to communicate with me when speech is unavailable", lines: 3 },
            { label: "People I can contact, and how", lines: 3 },
          ],
        },
        {
          slug: "professional-support",
          kind: "learn",
          title: "When professional support may be helpful",
          summary: "What to look for, and what to walk away from.",
          minutes: 3,
          blocks: [
            {
              type: "list",
              title: "Reasons people reach out",
              items: [
                "Distress from past experiences that is affecting daily life now",
                "Burnout that has not lifted despite reduced demands",
                "Meltdowns or shutdowns that feel unsafe",
                "Wanting a formal assessment, or wanting to understand your own profile better",
                "Being in, or leaving, a relationship that has caused harm",
              ],
            },
            {
              type: "list",
              title: "Signs of a good fit",
              items: [
                "Neurodiversity-affirming: your neurotype is worked with, not treated as the problem",
                "Trauma-informed: pace, choice and consent are yours throughout",
                "Willing to adapt — written communication, shorter sessions, lower sensory load, camera off",
                "Does not ask you to mask, make eye contact, or sit still to be taken seriously",
              ],
            },
            {
              type: "note",
              text: "Recovery Trauma™ is not a clinical service and cannot assess or treat. If you are in crisis, please use the crisis support options in the app or contact your local emergency services.",
            },
          ],
        },
      ],
    },
  ],
};
